import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {zodResolver} from "@hookform/resolvers/zod";
import {useStandardLogin} from "@mytour/platform";
import {FloatingField} from "@mytour/ui/form";
import {useEffect, useState} from "react";
import {type Control, Controller, type FieldValues, type Path, useForm} from "react-hook-form";
import {Trans as Translate, useTranslation} from "react-i18next";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Spinner,
} from "reactstrap";
import {z} from "zod";
import "./LoginPage.scss";

const Link = (props) => <a href={props.to} {...props} />;

const REQUIRED = "Champ requis";

const loginSchema = z.object({
  username: z.string().min(1, REQUIRED),
  password: z.string().min(1, REQUIRED),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const {login, loginError, isLoggingIn} = useStandardLogin();
  const [showPassword, setShowPassword] = useState(false);
  const {t: translate} = useTranslation();
  const passwordToggleLabel = showPassword
    ? translate("login.form.password.hide", "Masquer le mot de passe")
    : translate("login.form.password.show", "Afficher le mot de passe");

  const {
    handleSubmit,
    control,
    setFocus,
    setValue,
    formState: {errors},
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {username: "", password: ""},
  });

  useEffect(() => {
    setFocus("username");
  }, [setFocus]);

  return (
    <div id="login-page" className="login-page">
      <div className="login-page__shell">
        <section className="login-page__panel login-page__panel--form">
          <div className="login-page__brand">
            <img src="/images/logo.svg" alt="MyTour" className="login-page__brand-logo" />
          </div>
          <div className="login-page__form-container">
            <h1 className="login-page__heading">
              {translate("login.heading", "Authentification")}
            </h1>
            <p className="login-page__intro">
              {translate(
                "login.description",
                "Retrouvez votre planning, vos clients et vos factures en un clic."
              )}
            </p>
            <Form className="login-page__form-fields" onSubmit={handleSubmit(handleFormSubmit)}>
              <FloatingField
                name="username"
                label={translate("global.form.username.label")}
                control={control}
                error={errors.username}
                disabled={isLoggingIn}
                required
              />
              <FloatingPasswordField
                name="password"
                label={translate("login.form.password")}
                control={control}
                error={errors.password}
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword((prev) => !prev)}
                toggleLabel={passwordToggleLabel}
                disabled={isLoggingIn}
                required
              />
              <div className="login-page__actions">
                <Button
                  color="primary"
                  tag={Link}
                  size="lg"
                  outline
                  to="https://mytour.tech/inscription/"
                  disabled={isLoggingIn}
                  className="login-page__button login-page__button--secondary"
                >
                  Inscription
                </Button>
                <Button
                  color="success"
                  size="lg"
                  type="submit"
                  disabled={isLoggingIn}
                  className="login-page__button login-page__button--primary"
                >
                  {isLoggingIn ? (
                    <Spinner size="sm" />
                  ) : (
                    <Translate i18nKey="login.form.button">Connexion</Translate>
                  )}
                </Button>
              </div>

              {loginError ? (
                <div className="login-page__error" id="login-error">
                  <div className="login-page__error-title">
                    <FontAwesomeIcon icon="triangle-exclamation" />
                    <strong>
                      {translate("login.messages.error.title", "Erreur d'authentification !")}
                    </strong>
                  </div>
                  <div>
                    {translate(
                      "login.messages.error.detail",
                      "Veuillez vérifier vos identifiants de connexion."
                    )}
                  </div>
                </div>
              ) : null}
            </Form>
          </div>
        </section>

        <section className="login-page__panel login-page__panel--visual" aria-hidden="true">
          <div className="login-page__visual-copy">
            <h2 className="login-page__visual-title">
              {translate("login.visual.title", "Toute votre activité sur une seule application.")}
            </h2>
            <p className="login-page__visual-description">
              {translate(
                "login.visual.description",
                "Planning, clients, factures — gérez tout depuis le terrain."
              )}
            </p>
          </div>
          <img src="/images/logo.svg" alt="" className="login-page__visual-logo" />
        </section>
      </div>
    </div>
  );

  async function handleFormSubmit(data: LoginFormData) {
    try {
      await login({...data, rememberMe: true});
    } catch (_error) {
      setValue("password", "");
      setFocus("password");
    }
  }
}

type FloatingPasswordFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  control: Control<T>;
  error?: {message?: string};
  showPassword: boolean;
  onTogglePassword: () => void;
  toggleLabel: string;
  disabled?: boolean;
  required?: boolean;
};

function FloatingPasswordField<T extends FieldValues>({
  name,
  label,
  control,
  error,
  showPassword,
  onTogglePassword,
  toggleLabel,
  disabled,
  required,
}: FloatingPasswordFieldProps<T>) {
  return (
    <div className="login-page__password-field">
      <InputGroup>
        <div className="form-floating flex-grow-1">
          <Controller
            name={name}
            control={control}
            render={({field}) => (
              <Input
                {...field}
                value={field.value ?? ""}
                type={showPassword ? "text" : "password"}
                disabled={disabled}
                invalid={!!error}
                placeholder={label}
                id={`field-${name}`}
              />
            )}
          />
          <Label for={`field-${name}`}>
            {label}
            {required && " *"}
          </Label>
        </div>
        <InputGroupText
          role="button"
          aria-label={toggleLabel}
          onClick={onTogglePassword}
          style={{cursor: "pointer"}}
        >
          <FontAwesomeIcon icon={showPassword ? "eye-slash" : "eye"} fixedWidth />
        </InputGroupText>
      </InputGroup>
      {error && <FormFeedback style={{display: "block"}}>{error.message}</FormFeedback>}
    </div>
  );
}
